import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import type { ReactNode } from "react";

import { LEDDot } from "@/components/ui/LEDDot";
import { api, type SummaryResponse } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated } from "@/lib/pb";
import { useCountUp } from "@/lib/useCountUp";

export const Route = createFileRoute("/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: DashboardHome,
});

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

function DashboardHome() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["summary"],
    queryFn: () => api.summary.read(),
    refetchInterval: 60_000,
  });

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <p className="path-chip">/home</p>
          <h1 className="text-3xl font-semibold mt-1">Overview</h1>
        </div>
        {data && (
          <p className="font-mono text-xs text-muted">
            actualizado {formatRelativeTime(data.fetched_at)}
          </p>
        )}
      </div>

      {error && (
        <div className="surface px-4 py-3 font-mono text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {isLoading && <SkeletonGrid />}

      {data && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}
        >
          <AnalyticsCard data={data.analytics} />
          <ApprovalsCard data={data.approvals} />
          <AgentsCard data={data.agents} />
          <BlogCard data={data.blog} />
          <ReportsCard data={data.reports} />
        </motion.div>
      )}
    </div>
  );
}

// ─── shell ──────────────────────────────────────────────────────────────

interface TileProps {
  to: string;
  label: string;
  state: "ok" | "warn" | "danger" | "info" | "off";
  available: boolean;
  error?: string;
  children: ReactNode;
}

function Tile({ to, label, state, available, error, children }: TileProps) {
  return (
    <motion.div variants={fade}>
      <Link
        to={to}
        className="surface p-4 block h-full transition-all hover:border-violet/40 hover:shadow-[0_0_24px_rgba(168,85,247,0.12)]"
      >
        <div className="flex items-center justify-between">
          <span className="path-chip">{label}</span>
          <LEDDot state={available ? state : "off"} />
        </div>
        {available ? (
          children
        ) : (
          <p className="mt-3 font-mono text-xs text-muted/70 leading-relaxed">
            sin datos{error ? ` — ${error}` : ""}
          </p>
        )}
      </Link>
    </motion.div>
  );
}

function Metric({ value }: { value: number }) {
  const shown = useCountUp(value);
  return <span className="text-3xl font-mono">{Math.round(shown).toLocaleString("es-CR")}</span>;
}

function Delta({ value, unit = "" }: { value?: number; unit?: string }) {
  if (value == null) return null;
  const flat = value === 0;
  return (
    <span
      className={cn("font-mono text-xs", flat ? "text-muted" : value > 0 ? "text-success" : "text-danger")}
    >
      {flat ? "→" : value > 0 ? "↑" : "↓"} {Math.abs(value).toLocaleString("es-CR")}
      {unit}
    </span>
  );
}

// ─── cards ──────────────────────────────────────────────────────────────

function AnalyticsCard({ data }: { data: SummaryResponse["analytics"] }) {
  return (
    <Tile
      to="/analytics"
      label="Analytics"
      state={data.stale ? "warn" : "ok"}
      available={data.available}
      error={data.error}
    >
      <div className="mt-2 flex items-end gap-2">
        <Metric value={data.clicks ?? 0} />
        <span className="text-muted text-sm mb-1">clicks</span>
        <span className="ml-auto mb-1">
          <Delta value={data.delta?.clicks} />
        </span>
      </div>
      <p className="mt-2 font-mono text-xs text-muted">
        {(data.impressions ?? 0).toLocaleString("es-CR")} impresiones · pos {data.position ?? "—"} ·{" "}
        {data.period ?? ""}
        {data.stale && <span className="text-warn"> · cache viejo</span>}
      </p>
    </Tile>
  );
}

function ApprovalsCard({ data }: { data: SummaryResponse["approvals"] }) {
  const pending = data.pending ?? 0;
  return (
    <Tile
      to="/approvals"
      label="Approvals"
      state={data.failed ? "danger" : pending ? "warn" : "ok"}
      available={data.available}
      error={data.error}
    >
      <div className="mt-2 flex items-end gap-2">
        <Metric value={pending} />
        <span className="text-muted text-sm mb-1">pendientes</span>
      </div>
      <p className="mt-2 font-mono text-xs text-muted">
        {data.failed ? `${data.failed} fallidos · ` : ""}
        {data.oldest ? `más viejo ${formatRelativeTime(data.oldest)}` : "cola limpia"}
      </p>
    </Tile>
  );
}

function AgentsCard({ data }: { data: SummaryResponse["agents"] }) {
  const latest = data.latest;
  const active = data.active ?? 0;
  return (
    <Tile
      to="/agents"
      label="Agents"
      state={active ? "info" : latest?.status === "failure" ? "danger" : "ok"}
      available={data.available}
      error={data.error}
    >
      <div className="mt-2 flex items-end gap-2">
        <Metric value={active} />
        <span className="text-muted text-sm mb-1">activas</span>
      </div>
      <p className="mt-2 font-mono text-xs text-muted truncate">
        {latest
          ? `${latest.mode} · ${latest.agent_status || latest.status}${
              latest.started_at ? ` · ${formatRelativeTime(latest.started_at)}` : ""
            }`
          : "sin corridas todavía"}
      </p>
    </Tile>
  );
}

function BlogCard({ data }: { data: SummaryResponse["blog"] }) {
  return (
    <Tile to="/blog" label="Blog" state="ok" available={data.available} error={data.error}>
      <div className="mt-2 flex items-end gap-2">
        <Metric value={data.total_posts ?? 0} />
        <span className="text-muted text-sm mb-1">posts</span>
      </div>
      <p className="mt-2 font-mono text-xs text-muted truncate">
        {data.posts_with_perf ?? 0} con datos GSC ·{" "}
        {(data.total_clicks ?? 0).toLocaleString("es-CR")} clicks
      </p>
      {data.latest && (
        <p className="mt-1 text-xs text-muted/70 truncate" title={data.latest.title}>
          último: {data.latest.title}
        </p>
      )}
    </Tile>
  );
}

function ReportsCard({ data }: { data: SummaryResponse["reports"] }) {
  return (
    <Tile
      to="/reports"
      label="Reports"
      state={data.action_count ? "warn" : "ok"}
      available={data.available}
      error={data.error}
    >
      <div className="mt-2 flex items-end gap-2">
        <Metric value={data.action_count ?? 0} />
        <span className="text-muted text-sm mb-1">acciones abiertas</span>
      </div>
      <p className="mt-2 font-mono text-xs text-muted">
        {data.latest_week ?? "—"} · {data.total_weeks ?? 0} semanas
        {data.has_next_topic && <span className="text-violet"> · próximo tema listo</span>}
      </p>
    </Tile>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="surface p-4 animate-pulse">
          <div className="h-3 w-20 bg-border rounded" />
          <div className="h-8 w-28 bg-border/60 rounded mt-4" />
          <div className="h-3 w-40 bg-border/40 rounded mt-3" />
        </div>
      ))}
    </div>
  );
}
